import { PkButton } from '@uefadigital/panenka-react';
import useThrottledCallback from 'beautiful-react-hooks/useThrottledCallback';
import useWindowScroll from 'beautiful-react-hooks/useWindowScroll';
import clsx from 'clsx';
import { memo, useState, useRef, Suspense, lazy, useEffect } from 'react';

import ErrorBoundary from '../ErrorBoundary';
import { useMatchStream } from '../hooks/useMatchStream';

import { useWindowSize } from '@hooks/useWindowSize';
import { useTranslateFn } from '@shared/Translate';
import type { Match } from '@shared-types/match.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';

import * as classes from './styles.module.scss';
const VideoPlayer = lazy(() => import('../VideoPlayer'));
const Stream = lazy(() => import('./components/StreamPlayer'));
const YouTubeStream = lazy(() => import('./components/YouTubeStreamPlayer'));

const MatchStream = ({
  match,
  className,
  wrapperClassName,
  showMatchHighlights = true,
  blockHighlights,
  streamMessage,
  recalcHeader,
}: {
  match: Match;
  className?: string;
  wrapperClassName?: string;
  showMatchHighlights?: boolean;
  blockHighlights?: boolean;
  streamMessage?: boolean;
  recalcHeader?: () => void;
}) => {
  const container = useRef<HTMLDivElement>(null);
  const { stream, youTubeId, highlightsVideo } = useMatchStream(match, showMatchHighlights);
  const [top, setTop] = useState<number>(0);
  const t = useTranslateFn();

  const { isMobile } = useWindowSize();
  const onScroll = useWindowScroll();
  const [isAffix, setIsAffix] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const enableFloatingStreamRef = useRef<boolean>(true);

  onScroll(
    useThrottledCallback(
      () => {
        if (!container.current) return;
        const { top, bottom } = container.current.getBoundingClientRect();
        const stickyHeight = [
          ...(document.querySelectorAll('.navigation--sticky') ?? []),
          ...(document.querySelectorAll('.affix') ?? []),
        ].reduce((acc, elem) => acc + elem.clientHeight, 0);

        // on tablet and desktop, move stream as a sticky item when scrolling reaches the bottom
        // on mobile, move stream as a sticky item when scrolling reaches the top
        const affix = isMobile ? top < stickyHeight : bottom < stickyHeight;

        setTop(affix ? stickyHeight : null);
        setIsAffix(affix);
      },
      [isMobile],
      100,
    ),
  );

  const handleDismissVideoPlayerTracking = () => {
    GoogleTagManagerHelper.ga4Event('pip_close', { interaction: 'Close pip', section_reference: 'pip' });
  };

  const handleFloatingVideoPlayerTracking = () => {
    GoogleTagManagerHelper.ga4Event('pip_loaded', { interaction: 'Pip loaded', section_reference: 'pip' });
  };

  useEffect(() => {
    if (!isAffix) return;
    handleFloatingVideoPlayerTracking();
  }, [isAffix]);

  const closeStream = () => {
    setIsAffix(false);
    enableFloatingStreamRef.current = false;
    recalcHeader();
    handleDismissVideoPlayerTracking();
  };

  return (
    <ErrorBoundary>
      <div ref={container} className={className}>
        {(!!stream || highlightsVideo) && (
          <div
            pk-theme='dark'
            className={clsx(
              classes.wrapper,
              wrapperClassName,
              isAffix && enableFloatingStreamRef.current && classes.affix,
            )}
          >
            <div
              className={clsx({
                [classes.container]: true,
                'match-stream__container--affix': isAffix,
              })}
              style={top && isMobile && isAffix && enableFloatingStreamRef.current ? { top: top } : {}}
            >
              <PkButton
                className={clsx(classes.closeButton, isHovered && classes.hover)}
                onTouchStart={() => setIsHovered(true)}
                onTouchEnd={() => setIsHovered(false)}
                onPkClick={closeStream}
                appearance='outlined'
                highContrast
                width='120'
              >
                <span className='pk-button__label pk-ml--xs'>{t('hide_video')}</span>
              </PkButton>
              <Suspense>
                {highlightsVideo && <VideoPlayer teaser={highlightsVideo} blockHighlights={blockHighlights} />}
                {!highlightsVideo && !!youTubeId && <YouTubeStream youTubeId={youTubeId} />}
                {!highlightsVideo && !youTubeId && (
                  <Stream streamUrl={stream} match={match} streamMessage={streamMessage} />
                )}
              </Suspense>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default memo(MatchStream);
